/**
 * Block 13 PR-G Phase 4A — Section «Гарантия».
 *
 * Fields:
 *   - warrantyTerm (numeric input, default 24)
 *   - warrantyUnit chip-segment («месяцев» / «лет», default 'months')
 *
 * **Локализация label единиц:** Phase 4A показывает Russian labels везде
 * (UI язык — RU). Phase 5+ при добавлении KZ/UZ UI locales — locale-aware
 * labels {kk: «ай»/«жыл», uz: «oy»/«yil»}. Сейчас template language (договор
 * на kk/uz) не влияет на UI labels, только на PDF render.
 *
 * Reference: prototype `screen-2.13.2-contract.html` lines 459-480
 * (Сроки и гарантия section — Phase 4A разделил Срок изготовления → SectionCostAndTerms,
 * Гарантия → отдельная SectionWarranty per брифу).
 */

import { type Dispatch } from 'react';

import { SectionCard } from '@/features/calculator/anketa/components/SectionCard';
import { SectionHeader } from '@/features/calculator/anketa/components/SectionHeader';

import { ChipSegment } from '../components/ChipSegment';
import type { ContractAction, ContractFormState } from '../state/contractReducer';

const INPUT_CLASS =
  'w-full min-h-tap px-3 rounded-xl border-[1.5px] border-accent bg-card text-base text-ink ' +
  'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none placeholder:text-ink/40';

const LABEL_CLASS = 'block text-sm font-semibold text-ink/80 mb-1';

const WARRANTY_UNIT_OPTIONS = [
  { value: 'months' as const, label: 'месяцев' },
  { value: 'years' as const, label: 'лет' },
];

export interface SectionWarrantyProps {
  state: ContractFormState;
  dispatch: Dispatch<ContractAction>;
}

export function SectionWarranty({ state, dispatch }: SectionWarrantyProps) {
  function handleTermChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    const parsed = raw === '' ? 0 : Math.max(0, Number(raw) || 0);
    dispatch({ type: 'SET_FIELD', update: { path: 'warrantyTerm', value: parsed } });
  }

  return (
    <SectionCard testId="contract-section-warranty">
      <SectionHeader>Гарантия</SectionHeader>

      <div className="flex gap-2">
        <div className="flex-1">
          <label htmlFor="contract-warranty-term" className={LABEL_CLASS}>
            Срок (число)
          </label>
          <input
            id="contract-warranty-term"
            type="number"
            inputMode="numeric"
            min={1}
            value={state.warrantyTerm === 0 ? '' : state.warrantyTerm}
            onChange={handleTermChange}
            aria-label="Срок гарантии — число"
            data-testid="contract-warranty-term-input"
            className={INPUT_CLASS}
            placeholder="24"
          />
        </div>
        <div className="flex-1">
          <label className={LABEL_CLASS}>Единица</label>
          <ChipSegment
            options={WARRANTY_UNIT_OPTIONS}
            value={state.warrantyUnit}
            onChange={(v) => dispatch({ type: 'SET_WARRANTY_UNIT', value: v })}
            ariaLabel="Единица срока гарантии"
            name="warranty-unit"
          />
        </div>
      </div>
    </SectionCard>
  );
}
